import React, { useState } from 'react';
import { Card as MuiCard, CardMedia, CardContent, Chip, IconButton, Box, Typography } from '@mui/material';
import { FavoriteOutlined, Favorite, Visibility } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { API_ORIGIN } from '../../../config/api';

/**
 * Package Card Component with hover effects and animations
 * Booking.com inspired design
 */
const StyledCard = styled(motion(MuiCard))(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.custom.borderRadius.lg,
  overflow: 'hidden',
  boxShadow: theme.custom.boxShadow.md,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  
  '&:hover': {
    boxShadow: theme.custom.boxShadow.xl,
    transform: 'translateY(-8px)',
  },
}));

const resolveImageUrl = (img) => {
  if (!img) return 'https://via.placeholder.com/800x600?text=Travel+Package';
  if (img.startsWith('http')) return img;
  if (img.startsWith('/')) return `${API_ORIGIN}${img}`;
  return `${API_ORIGIN}/${img}`;
};
const ImageContainer = styled(Box)({
  position: 'relative',
  paddingTop: '66.67%', // 3:2 aspect ratio
  overflow: 'hidden',
});

const StyledCardMedia = styled(CardMedia)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease-in-out',
  
  '&:hover': {
    transform: 'scale(1.1)',
  },
});

const BadgeContainer = styled(Box)({
  position: 'absolute',
  top: 12,
  left: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  zIndex: 2,
});

const FavoriteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  zIndex: 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.05)',
  },
}));

const GradientOverlay = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '50%',
  background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.6) 100%)',
  zIndex: 1,
});

const PackageCard = ({
  image,
  title,
  description,
  price,
  duration,
  rating,
  badges = [],
  onFavoriteToggle,
  onQuickView,
  onClick,
  loading = false,
  isFavorite = false,
  currency,
  category,
}) => {
  const [favorite, setFavorite] = useState(isFavorite);
  const resolvedImage = resolveImageUrl(image);
  const isPkr =
    currency === 'PKR' ||
    category === 'Pakistan' ||
    category === 'Local' ||
    /pakistan|swat|hunza|skardu|murree|lahore|naran|kaghan/i.test((title || '') + ' ' + (description || ''));
  const formattedPrice = isPkr
    ? `PKR ${Number(price || 0).toLocaleString()}`
    : `$${Number(price || 0).toLocaleString()}`;

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    setFavorite(!favorite);
    onFavoriteToggle && onFavoriteToggle(!favorite);
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    onQuickView && onQuickView();
  };

  if (loading) {
    return (
      <MuiCard sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Skeleton height={300} />
        <CardContent>
          <Skeleton height={24} width="80%" style={{ marginBottom: 8 }} />
          <Skeleton height={16} width="100%" count={2} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Skeleton height={32} width={100} />
            <Skeleton height={32} width={80} />
          </Box>
        </CardContent>
      </MuiCard>
    );
  }

  const motionProps = {
    whileHover: { y: -8 },
    transition: { duration: 0.3 },
  };

  return (
    <StyledCard {...motionProps} onClick={onClick}>
      <ImageContainer>
        <StyledCardMedia
          component="img"
          image={resolvedImage}
          alt={title}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/800x600?text=Umrah+Package';
          }}
        />
        <GradientOverlay />
        
        <BadgeContainer>
          {badges.map((badge, index) => (
            <Chip
              key={index}
              label={badge.label}
              size="small"
              sx={{
                backgroundColor: badge.color || 'primary.main',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          ))}
        </BadgeContainer>

        <FavoriteButton
          size="small"
          onClick={handleFavoriteClick}
        >
          {favorite ? (
            <Favorite sx={{ color: 'error.main', fontSize: 20 }} />
          ) : (
            <FavoriteOutlined sx={{ fontSize: 20 }} />
          )}
        </FavoriteButton>
      </ImageContainer>

      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {duration && (
            <Chip
              label={`${duration} days`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
          {rating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ⭐ {rating}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Starting from
            </Typography>
            <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
              {formattedPrice}
            </Typography>
          </Box>

          {onQuickView && (
            <IconButton
              size="small"
              onClick={handleQuickView}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default PackageCard;
